import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_TOKEN } from 'constants/constants';
import { IPagination } from 'interfaces/table';
import {
  IDealerObject,
  IDealerUserObject,
} from 'pages/Shared/Dealer/Models/dealers';

export interface IDealerConfigRequest {
  ticketId: string;
  initialCall: boolean;
  isTestMode?: boolean;
  configJson: string;
}

export interface IVerifyConfigResultComponentsRequest {
  dealerId: string;
  mandatoryComponentsIds: string[];
  optionalComponentsIds: string[];
  mandatoryThenOptionalComponentsIds: string[];
}

export interface IDealerConfigProps {
  configurationType: string;
  draftConfiguration: string;
  draftTicketId: string;
  prodConfiguration: string;
  isInitialCall: boolean;
  isTestMode: boolean;
}

export interface IInviteDealerUser {
  props: string;
  invitedDealerId: number;
  email: string;
}

export interface IDealerListView {
  id: number;
  dealerId: string;
  dealerName: string;
}

export interface IDealerOptionsWithTickets {
  id: number;
  dealerId: string;
  dealerName: string;
  hasTickets: boolean;
}

export interface IDealer extends IPagination {
  dealerId: string;
  sortByColumn: string;
  sortOrder: string;
  search: string;
  isArchived: boolean | undefined;
}

export interface ITicket {
  ticketId: string;
}

export interface ITicketCustomer {
  id: number;
  company: string;
  lastName: string;
}

export interface IDealerTicket {
  ticketId: string;
  productText: string;
  activationStatus: string;
  expiryDate: string;
  licenseQuantity: string;
  maintenanceExpiry: string;
  parentDealerId: string;
  dealerId: string;
  dealerName: string;
  isWibuUpdateAvailable: boolean;
  minimumExpiryDate: string;
  diffDays?: number;
  status?: string;
  customer: ITicketCustomer;
  dealerTicketId: number;
  isArchived?: boolean;
  fmCustomerId?: string;
  fmCustomerDescription?: string;
}
export interface IDealerTicketCustomer {
  dealerTicketId: number;
  customerId: number;
}

export interface IDealerFiles {
  dealerId: string;
  baseUrl: string;
}

export interface IDealerTicketArchive {
  ticketId: string;
  isArchived: boolean;
}

export interface IDealerTicketsBatchArchive {
  dealerTicketIds: number[];
  isArchived: boolean;
}

export interface ITicketsBatchArchiveResult {
  totalCount: number;
  successCount: number;
  failureCount: number;
}

export interface IDealerTicketsBatchAction {
  dealerTicketsDbIds: number[];
  action: string;
}

export interface IDealerAssignTicket {
  dealerId: string;
  tickets: string[];
  licenseAdminUser: string;
  oldDealerId?: string;
  fmCustomerId?: string;
  fmCustomerDescription?: string;
}

export interface IDealerTicketsResponse {
  dealerId: string;
  isRootDealer: boolean;
  count: number;
  tickets: IDealerTicket[];
}

export interface IActiveProductDetailResponse {
  licenseDetails: any;
}

const token = localStorage.getItem(API_TOKEN);

export const dealerApi = createApi({
  reducerPath: 'dealerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_ACTIVITY_BASE_URL,
    prepareHeaders: (headers) => {
      const authToken = localStorage.getItem(API_TOKEN);
      if (authToken) {
        headers.set('Authorization', `Bearer ${authToken}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    'Dealers',
    'OneDealer',
    'UserDealer',
    'DealerConfig',
    'DealerTickets',
    'ActiveProductDetail',
  ],
  endpoints: (builder) => ({
    getDealerList: builder.query<[], string>({
      query: (props) => `/v1/dealers-informations?props=${props}`,
      providesTags: ['Dealers'],
    }),
    gerBranchDealerList: builder.query<[], number>({
      query: (id) => `/v1/branch-dealers-informations/${id}`,
      providesTags: ['Dealers'],
    }),
    getOneDealer: builder.query<IDealerObject, number | string>({
      query: (id) => `/v1/private/dealers-informations/${id}`,
      providesTags: ['OneDealer'],
    }),
    getDealerByUserId: builder.query<IDealerObject, number>({
      query: (id) => `/v1/dealers-informations-user/${id}`,
      providesTags: ['UserDealer'],
    }),
    getSubDealersByBranchDealerUser: builder.query<
      [IDealerOptionsWithTickets],
      void
    >({
      query: () => `/v1/branch/dealers`,
    }),
    addNewDealer: builder.mutation<[], IDealerObject>({
      query: (data) => {
        const formData = new FormData();
        const fileIsSelected = !!data?.files?.configFile;
        if (fileIsSelected) {
          formData.append('files.fixedConfigFile', data?.files?.configFile[0]);
        }
        const logoIsSelected = !!data.files?.logo;
        if (logoIsSelected) {
          formData.append('files.logo', data.files?.logo[0]);
        }
        formData.append('data', JSON.stringify({ ...data, files: undefined }));
        return {
          url: '/v1/dealers-informations',
          method: 'Post',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['Dealers', 'OneDealer'],
    }),
    updateDealer: builder.mutation<[], IDealerObject>({
      query: (data) => {
        const formData = new FormData();
        const fileIsSelected = !!data?.files?.configFile;
        const { fixedConfigFile, ...restConfiguration } = data.configuration;
        const cleanedData = {
          ...data,
          configuration: restConfiguration,
        };
        if (fileIsSelected) {
          formData.append('files.fixedConfigFile', data?.files?.configFile[0]);
        }
        const logoIsSelected = !!data.files?.logo;
        if (logoIsSelected) {
          formData.append('files.logo', data.files?.logo[0]);
        }
        formData.append(
          'data',
          JSON.stringify({ ...cleanedData, files: undefined })
        );
        return {
          url: `/v1/dealers-informations/${data.id}`,
          method: 'Put',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: ['Dealers', 'OneDealer'],
    }),
    updateDealerProdConfiguration: builder.mutation<[], { configJson: string }>(
      {
        query: (data) => {
          return {
            url: `/v1/dealers-informations/push/config/to/production`,
            method: 'Post',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: data,
          };
        },
      }
    ),
    updateDealerUserId: builder.mutation<[], IDealerUserObject>({
      query: (data) => {
        return {
          url: `/v1/dealers-informations-user/${data.id}`,
          method: 'Put',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: data,
        };
      },
      invalidatesTags: ['Dealers', 'OneDealer'],
    }),
    getDealerConfigResult: builder.mutation<[], IDealerConfigRequest>({
      query: (data) => {
        return {
          url: `/v1/dealers-informations/test/installation/config`,
          method: 'Post',
          body: data,
        };
      },
      invalidatesTags: ['DealerConfig'],
    }),
    verifyConfigResultComponents: builder.mutation<
      [],
      IVerifyConfigResultComponentsRequest
    >({
      query: (data) => {
        return {
          url: `/v1/dealers-informations/components/verify`,
          method: 'Post',
          body: data,
        };
      },
      invalidatesTags: ['DealerConfig'],
    }),
    inviteAsDealerUser: builder.mutation<[], IInviteDealerUser>({
      query: (data) => {
        return {
          url: `/v1/invite-dealers`,
          method: 'Post',
          body: data,
        };
      },
      invalidatesTags: ['Dealers', 'OneDealer'],
    }),
    getDealerTickets: builder.query<IDealerTicketsResponse, IDealer>({
      query: (data) => {
        const params = [
          `_start=${(data.page - 1) * data.sizePerPage}`,
          `_limit=${data.sizePerPage}`,
          `_sort=${data.sortByColumn}:${data.sortOrder}`,
          `search=${data.search}`,
        ];
        if (data.isArchived !== null && data.isArchived !== undefined) {
          params.push(`isArchived=${data.isArchived}`);
        }
        return {
          url: `/v2/dealer-tickets?${params.join('&')}`,
          method: 'Post',
          body: data,
        };
      },
      providesTags: ['DealerTickets'],
    }),
    updateDealerTickets: builder.mutation<[], IDealerTicketCustomer>({
      query: (data) => {
        return {
          url: `v1/dealer-tickets/customer`,
          method: 'Post',
          body: data,
        };
      },
      invalidatesTags: ['DealerTickets'],
    }),
    assignTicketToDealer: builder.mutation<[], IDealerAssignTicket>({
      query: (data) => {
        return {
          url: `v2/link/dealer-tickets`,
          method: 'Post',
          body: data,
        };
      },
      invalidatesTags: ['DealerTickets'],
    }),
    fetchDealerTicketActiveProductDetail: builder.mutation<
      IActiveProductDetailResponse,
      ITicket
    >({
      query: (data) => {
        return {
          url: `/v1/active-product-details`,
          method: 'Post',
          body: data,
        };
      },
      invalidatesTags: ['ActiveProductDetail'],
    }),
    setArchiveDealerTickets: builder.mutation<[], IDealerTicketArchive>({
      query: (data) => {
        return {
          url: `/v1/wibu-license-center-infos/archive-ticket`,
          method: 'Post',
          body: data,
        };
      },
      invalidatesTags: ['DealerTickets'],
    }),
    generateComponentFile: builder.mutation<[], IDealerFiles>({
      query: (data) => {
        return {
          url: `v1/dealers-informations/components/xml`,
          method: 'Post',
          body: data,
        };
      },
      invalidatesTags: ['OneDealer'],
    }),
    batchArchiveDealerTickets: builder.mutation<
      ITicketsBatchArchiveResult,
      IDealerTicketsBatchAction
    >({
      query: (data) => {
        return {
          url: `/v1/dealer-tickets/batch-actions`,
          method: 'Post',
          body: data,
        };
      },
      invalidatesTags: ['DealerTickets'],
    }),
  }),
});

export const {
  useGetDealerListQuery,
  useGerBranchDealerListQuery,
  useGetOneDealerQuery,
  useGetDealerByUserIdQuery,
  useAddNewDealerMutation,
  useUpdateDealerMutation,
  useUpdateDealerUserIdMutation,
  useGetDealerConfigResultMutation,
  useInviteAsDealerUserMutation,
  useGetDealerTicketsQuery,
  // useFetchDealerTicketsMutation,
  useUpdateDealerTicketsMutation,
  useAssignTicketToDealerMutation,
  useFetchDealerTicketActiveProductDetailMutation,
  useGetSubDealersByBranchDealerUserQuery,
  useUpdateDealerProdConfigurationMutation,
  useSetArchiveDealerTicketsMutation,
  useGenerateComponentFileMutation,
  useVerifyConfigResultComponentsMutation,
  useBatchArchiveDealerTicketsMutation,
} = dealerApi;
